import Vue from 'vue';
import * as InputTypes from '../components/inputs/_types';

import { VuexModule, Mutation, Action, Module } from 'vuex-module-decorators';
import { JobAdditionalItem } from '@gid/models';
import {
  plainApiToModel,
  modelToPlainApi,
} from './shared/api-model-conversion';

export const moduleName = InputTypes.ADDITIONAL_ITEMS;

export enum JobAdditionalItemsActionsEnum {
  SET_ITEMS = 'SET_ITEMS',
  CLEAR_TEMS = 'CLEAR_TEMS',
  ADD_ITEM = 'ADD_ITEM',
  REMOVE_ITEM = 'REMOVE_ITEM',
}

export interface JobAdditionalItemsBasePayload {
  jobId: string;
}

export interface JobAdditionalItemsSetPayload
  extends JobAdditionalItemsBasePayload {
  items: JobAdditionalItem[];
}

export interface JobAdditionalItemsAddItemPayload
  extends JobAdditionalItemsBasePayload {
  item: JobAdditionalItem;
}

export interface JobAdditionalItemsRemoveItemPayload
  extends JobAdditionalItemsBasePayload {
  byIndex?: number;
  byLinkId?: string;
}

@Module({ name: moduleName, namespaced: true })
export class JobAdditionalItems extends VuexModule {
  // Declare state
  items: {
    [key: string]: JobAdditionalItem[];
  } = {};

  get forJob() {
    return (jobId) => this.items[jobId] ?? [];
  }

  get inputData() {
    return (jobId) => modelToPlainApi(this.items[jobId]);
  }

  get priceListsSum() {
    return (jobId) =>
      this.forJob(jobId).reduce(
        (sum, item) => sum + (item.priceListsSum ?? 0),
        0,
      );
  }

  get priceSellingSum() {
    return (jobId) =>
      this.forJob(jobId).reduce(
        (sum, item) => sum + (item.priceSellingSum ?? 0),
        0,
      );
  }

  get pricePurchasingSum() {
    return (jobId) =>
      this.forJob(jobId).reduce(
        (sum, item) => sum + (item.pricePurchasingSum ?? 0),
        0,
      );
  }

  @Mutation
  setItems(payload: JobAdditionalItemsSetPayload) {
    Vue.set(this.items, payload.jobId, plainApiToModel(payload.items));
  }

  @Mutation
  clearItems(payload: JobAdditionalItemsBasePayload) {
    Vue.delete(this.items, payload.jobId);
  }

  @Mutation
  addItem(payload: JobAdditionalItemsAddItemPayload) {
    if (!this.items[payload.jobId]) {
      this.items[payload.jobId] = [];
    }
    this.items[payload.jobId].push(payload.item);
  }

  @Mutation
  removeItem(payload: JobAdditionalItemsRemoveItemPayload) {
    if (!this.items[payload.jobId]) {
      return;
    }
    const index =
      payload.byIndex ??
      this.items[payload.jobId].findIndex(
        (item) => item.linkId === payload.byLinkId,
      );
    if (index >= 0) {
      Vue.delete(this.items[payload.jobId], index);
    }
  }

  @Action({ commit: 'setItems' })
  [JobAdditionalItemsActionsEnum.SET_ITEMS](
    payload: JobAdditionalItemsSetPayload,
  ) {
    return payload;
  }

  @Action({ commit: 'clearItems' })
  [JobAdditionalItemsActionsEnum.CLEAR_TEMS](
    payload: JobAdditionalItemsBasePayload,
  ) {
    return payload;
  }

  @Action({ commit: 'addItem' })
  [JobAdditionalItemsActionsEnum.ADD_ITEM](
    payload: JobAdditionalItemsAddItemPayload,
  ) {
    return payload;
  }

  @Action({ commit: 'removeItem' })
  [JobAdditionalItemsActionsEnum.REMOVE_ITEM](
    payload: JobAdditionalItemsRemoveItemPayload,
  ) {
    return payload;
  }
}

export default JobAdditionalItems;
